import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { useDispatch } from 'react-redux';
import { Link, useLoaderData } from 'react-router-dom';

import { surveyTypesAsNames } from 'unihow-common';
import { addErrorMessageAction, setTaskFulfilled, setTaskPending } from '../../../actions';
import { putUserRoles } from '../../../api';
import { ROLE_KIND_ORDER, routes, SURVEY_TYPE_ORDER } from '../../../constants';
import { langType } from '../../../constants/types';
import { withLang } from '../../../i18n';
import { getCommonName, setDocumentTitle } from '../../../util';

import styles from './user.module.css';

const InfoRow = ({ label, content }) => (
  <div className={styles.row}>
    <div className={styles.label}>{label}</div>
    <div className={styles.info}>{content}</div>
  </div>
);

const RoleItem = ({
  roleName,
  disabled,
  onRoleChange,
  checked,
  roleLabel,
  className
}) => (
  <div className={className || 'form__item'}>
    <input
      type="checkbox"
      id={`role_${roleName}`}
      name="role"
      value={roleName}
      disabled={disabled}
      checked={checked}
      onChange={onRoleChange}
    />
    {roleLabel ? (
      <label htmlFor={`role_${roleName}`} className={styles.roleLabel}>
        {roleLabel}
      </label>
    ) : null}
  </div>
);

const User = ({ translate: t }) => {
  const dispatch = useDispatch();
  const {
    user: loaderUser,
    roles
  } = useLoaderData();

  const [user, setUser] = useState(loaderUser);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    setDocumentTitle(t('permissionManagement.user'));
  }, [t]);

  const { generalRoles, surveyMatrix } = useMemo(() => {
    if (!roles.length) {
      return { generalRoles: [], surveyMatrix: {} };
    }

    const general = [];
    const matrix = {};

    roles.forEach((role) => {
      const { surveyType, roleKind } = role;

      if (!role?.assignable) return;

      if (!surveyType) {
        general.push(role);
      } else {
        if (!matrix[surveyType]) {
          matrix[surveyType] = {};
        }
        matrix[surveyType][roleKind] = role;
      }
    });

    return { generalRoles: general, surveyMatrix: matrix };
  }, [roles]);

  const onRoleChange = (event) => {
    setSaveSuccess(false);
    const { value, checked } = event.target;
    const currentRoles = Array.isArray(user.roles) ? user.roles : [];
    let nextRoles;

    if (checked) {
      nextRoles = currentRoles.includes(value)
        ? currentRoles
        : [...currentRoles, value];
    } else {
      nextRoles = currentRoles.filter((r) => r !== value);
    }

    setUser((prev) => ({ ...prev, roles: nextRoles }));
  };

  const onSave = async () => {
    try {
      dispatch(setTaskPending());
      const updatedRoles = await putUserRoles(user.userId, user.roles || []);

      setUser((prev) => ({
        ...prev,
        roles: updatedRoles
      }));

      setSaveSuccess(true);
    } catch (err) {
      dispatch(addErrorMessageAction(err));
    } finally {
      dispatch(setTaskFulfilled());
    }
  };

  const displayName = getCommonName(user);

  return (
    <div className="page-container">
      <h1 className={styles.header}>{`${t('permissionManagement.user')} ${displayName}`}</h1>

      {/* Basic info */}
      <div className={styles.infoBox}>
        <h2>{t('permissionManagement.basicInfo')}</h2>
        <InfoRow label={t('common.name')} content={displayName} />
        <InfoRow label={t('common.username')} content={user.username || ''} />
        <InfoRow label={t('common.email')} content={user.email || ''} />
        {user.studentNumber && (
          <InfoRow label={t('common.studentNumber')} content={user.studentNumber} />
        )}
      </div>

      {/* General roles */}
      <div className={styles.infoBox}>
        <h2>{t('permissionManagement.generalRoles')}</h2>
        <div className={styles.info}>
          <div className={styles.roleGrid}>
            {generalRoles.map((role) => (
              <RoleItem
                key={role.name}
                roleName={role.name}
                disabled={!role.assignable}
                checked={Array.isArray(user.roles)
                  && user.roles.includes(role.name)}
                onRoleChange={onRoleChange}
                roleLabel={t(`generalRoles.${role.name}`)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Survey roles matrix */}
      <div className={styles.infoBox}>
        <h2>{t('permissionManagement.surveyRoles')}</h2>
        <div>
          <table className={styles.matrixTable}>
            <thead>
              <tr>
                <th className={styles.matrixHeaderSurveyType}>
                  <Translate id="common.type" />
                </th>
                {ROLE_KIND_ORDER.map((kind) => (
                  <th key={kind} className={styles.matrixHeaderCell}>
                    {t(`roleKinds.${kind}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SURVEY_TYPE_ORDER.filter((st) => surveyMatrix[st]).map((surveyType) => (
                <tr key={surveyType}>
                  <td className={styles.matrixSurveyTypeCell}>{surveyTypesAsNames[surveyType]}</td>
                  {ROLE_KIND_ORDER.map((kind) => {
                    const role = surveyMatrix[surveyType][kind];
                    if (!role) {
                      return (
                        <td
                          key={`${surveyType}-${kind}`}
                          className={styles.matrixCell}
                        />
                      );
                    }

                    return (
                      <td
                        key={role.name}
                        className={styles.matrixCell}
                      >
                        <RoleItem
                          roleName={role.name}
                          disabled={!role.assignable}
                          checked={Array.isArray(user.roles)
                            && user.roles.includes(role.name)}
                          onRoleChange={onRoleChange}
                          className={styles.matrixCheckboxWrapper}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={`${styles.saveSuccess} ${saveSuccess && styles.visible}`}>
        <p>{t('permissionManagement.saveSuccess')}</p>
      </div>

      <div className={styles.navigation}>
        <Link className="button--outline button--action" to={routes.adminTools.index}>
          {t('common.back')}
        </Link>
        <button
          type="button"
          className="button--action"
          onClick={onSave}
          data-cy="save-button"
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
};

const { string, func, bool } = PropTypes;

User.propTypes = {
  translate: func.isRequired,
  ...langType
};

InfoRow.propTypes = {
  label: string.isRequired,
  content: string.isRequired
};

RoleItem.propTypes = {
  roleName: string.isRequired,
  disabled: bool.isRequired,
  onRoleChange: func.isRequired,
  checked: bool.isRequired,
  roleLabel: string,
  className: string
};

RoleItem.defaultProps = {
  className: null,
  roleLabel: null
};

export default withLang(User);
